/**
 * Sentry client-side noise filters for Meta in-app-browser injected scripts.
 *
 * Extracted from instrumentation-client.ts so the pattern list and the
 * platform gate are unit-testable (follows the lib/csp.ts and
 * lib/messageRedirects.ts precedent).
 *
 * ## Why these exist
 *
 * Facebook and Instagram render links in their own embedded WebView and inject
 * their own instrumentation scripts into the page. Those scripts throw, the
 * throws reach `window.onerror` / `onunhandledrejection`, and Sentry's browser
 * integration captures them as if they were ours. Over the 30 days to
 * 2026-08-16, 66 of 67 production error events in `accesshunter-front` came
 * from these two browsers; the 67th was the deliberate T-137 round-trip test.
 *
 * ## WEB BUILD ONLY — the gate is the safety property, not the patterns
 *
 * Every justifying event came from the **web** build. In the Capacitor native
 * shell these same strings mean the opposite thing:
 *
 *   - `window.webkit.messageHandlers.bridge` is the ONLY transport to native on
 *     iOS (`@capacitor/android/.../native-bridge.js:858`, and note it is NOT
 *     optional-chained there).
 *   - `Error invoking postMessage: Java object is gone` is Android WebView's
 *     generic `@JavascriptInterface` teardown error. Capacitor's own bridge is
 *     such an object (`native-bridge.js:845`, `win.androidBridge.postMessage`),
 *     so in the packaged Android app that exact string means social login and
 *     deep links are broken.
 *
 * Message text cannot separate those from Meta's noise — they are byte-for-byte
 * identical. `isNativeShell()` is therefore what makes this change safe, and it
 * fails toward reporting: any sign of a native bridge disables all filtering.
 *
 * ## Why filtering matters beyond tidiness
 *
 * The frames are minified offsets (`app:///:1:4197`) that shift on every
 * deploy, so Sentry mints a *new* issue group for the same non-bug on each
 * release. Five separate groups existed for one injected script. Without this
 * filter the alert channel keeps firing on noise indefinitely and real
 * regressions get lost in it.
 *
 * ## Deliberate limits
 *
 * 1. Patterns match on message text, so in the **web build** a genuine bug of
 *    ours — or of a dependency — that produced one of these strings would also
 *    be suppressed. `messageHandlers` is absent from our own source, but a
 *    dependency's bridge is not covered by that check.
 * 2. `ignoreErrors` is a global event processor: it applies to explicit
 *    `Sentry.captureException` calls too (`app/error.tsx`,
 *    `app/global-error.tsx`), not only to global handlers.
 * 3. Only the client SDK is filtered. `sentry.server.config.ts` and
 *    `sentry.edge.config.ts` deliberately carry no equivalent — these strings
 *    are browser-engine and Android-WebView specific and no Node or edge
 *    runtime can produce them. Do not "fix" that inconsistency by copying
 *    these arrays there.
 */

/** Minimal shape of the bridges Capacitor installs. */
export interface NativeBridgeProbe {
  Capacitor?: { isNativePlatform?: () => boolean };
  androidBridge?: unknown;
  webkit?: { messageHandlers?: { bridge?: unknown } };
}

/**
 * True when running inside the Capacitor native shell.
 *
 * Probes the bridges directly rather than importing `@capacitor/core`, for two
 * reasons: this runs in the Next.js instrumentation entry, which loads before
 * the app bundle and should not pull in the Capacitor runtime; and the probe
 * still works if `@capacitor/core` has not initialised yet, whereas
 * `Capacitor.isNativePlatform()` would not.
 *
 * Any positive signal wins. Misreading native as web would re-enable
 * suppression of real bridge failures, so ambiguity resolves toward "native"
 * (i.e. toward reporting everything).
 */
export function isNativeShell(win: unknown): boolean {
  // Takes `unknown` rather than NativeBridgeProbe so a real `window` can be
  // passed without a cast: the probe's properties are all optional, which makes
  // it a TypeScript *weak type*, and `Window` shares none of them.
  if (!win || typeof win !== "object") return false;
  const w = win as NativeBridgeProbe;
  if (w.Capacitor?.isNativePlatform?.() === true) return true;
  if (w.androidBridge !== undefined) return true;
  if (w.webkit?.messageHandlers?.bridge !== undefined) return true;
  return false;
}

/**
 * Web-build patterns for `Sentry.init({ ignoreErrors })`.
 *
 * Every entry is grounded in a message captured in production. Do not add a
 * speculative pattern: an unobserved pattern cannot be justified against a real
 * string, and each one widens what we go blind to. A third entry for the
 * Chromium phrasing (`Cannot read properties of undefined (reading
 * 'messageHandlers')`) was deliberately removed — it was never observed, and it
 * would also swallow `maplibre-gl`'s `Actor.messageHandlers` failures, which is
 * a live dependency behind every map surface in the app.
 */
export const IN_APP_BROWSER_IGNORED_ERRORS: RegExp[] = [
  // iOS, Instagram + Facebook WebView. WebKit prints the whole evaluated
  // expression, so the `window.webkit.` receiver is present in the message:
  //   "undefined is not an object (evaluating 'window.webkit.messageHandlers')"
  /window\.webkit\.messageHandlers/,

  // Android, Meta's injected JS bridge torn down mid-call:
  //   "Error invoking postMessage: Java object is gone"
  //   "Error invoking enableButtonsClickedMetaDataLogging: Java object is gone"
  // Scoped to the "Error invoking <method>:" form rather than a bare
  // /Java object is gone/, which would also match unrelated prose. The method
  // name is deliberately left open (`\w+`) because `postMessage` is Meta's
  // busiest signature AND Capacitor's own — they are separated by the native
  // shell gate above, not by this pattern.
  /Error invoking \w+: Java object is gone/,
];

/**
 * Web-build patterns for `Sentry.init({ denyUrls })`.
 *
 * Sentry matches this against exactly ONE url per event — the throwing frame,
 * via `_getLastValidUrl()` on the root exception (`@sentry/core`
 * `integrations/eventFilters.js`). It is not a stack-wide filter.
 *
 * This entry is therefore defence-in-depth behind IN_APP_BROWSER_IGNORED_ERRORS
 * rather than the primary mechanism: the events it matches today already match
 * the Java pattern above. It earns its place by covering the case where Meta's
 * logger throws something new. Our own bundle is unaffected — `app:///_next/…`
 * has three slashes and cannot match the anchored two-slash pattern.
 */
export const IN_APP_BROWSER_DENIED_URLS: RegExp[] = [
  /^app:\/\/navigation_performance_logger_android/,
];

/** The `ignoreErrors` list to pass to `Sentry.init` for this platform. */
export function ignoredErrorsFor(win: unknown): RegExp[] {
  return isNativeShell(win) ? [] : IN_APP_BROWSER_IGNORED_ERRORS;
}

/** The `denyUrls` list to pass to `Sentry.init` for this platform. */
export function deniedUrlsFor(win: unknown): RegExp[] {
  return isNativeShell(win) ? [] : IN_APP_BROWSER_DENIED_URLS;
}

/**
 * Applies *this module's* patterns the way Sentry applies a regex entry —
 * `.test()` against one candidate string — so the list can be asserted against
 * real captured messages in tests.
 *
 * NOT a full mirror of the SDK, and must not be read as one. Sentry also merges
 * its own DEFAULT_IGNORE_ERRORS (11 patterns, one of them literally labelled
 * "Unactionable error in instagram webview") and ORs across three candidates
 * per event: `event.message`, the last `exception.values[].value`, and
 * `` `${type}: ${value}` ``. A `false` here proves only that *our* list lets a
 * message through, not that Sentry will keep it.
 */
export function matchesIgnoredError(message: string): boolean {
  return IN_APP_BROWSER_IGNORED_ERRORS.some((pattern) => pattern.test(message));
}
